import React, { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Select,
  Skeleton,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { alpha } from '@mui/material/styles';
import UserDashboard from 'layouts/UserDashboard';
import Container from 'components/Container';
import { getLoggedInUser } from 'auth/Login/AuthService';
import {
  getHousehold,
  getHouseholdInsights,
  getHouseholdSharing,
  inviteToHousehold,
  removeHouseholdMember,
  updateHouseholdSharing,
} from './FinancialService';
import {
  HouseholdMember,
  HouseholdSharing,
  HouseholdInsight,
  HouseholdSummary,
} from './types';
import FeatureGate from './FeatureGate';
import { FeatureKeys } from './FeatureKeys';

const C = {
  ink: '#17263d',
  muted: '#78869a',
  blue: '#1475d1',
  green: '#159b72',
  red: '#ff454d',
  orange: '#ff8b32',
};

const gbp = (n: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(n);

const ROLE_COLORS: Record<string, string> = {
  OWNER: C.blue,
  PARTNER: C.green,
  VIEWER: C.muted,
};

const INSIGHT_COLORS: Record<string, string> = {
  INFO: C.blue,
  WARNING: C.orange,
  POSITIVE: C.green,
};

// ─── RemoveMemberDialog ───────────────────────────────────────────────────────

interface RemoveMemberDialogProps {
  open: boolean;
  member: HouseholdMember | null;
  onClose: () => void;
  onRemoved: () => void;
}

const RemoveMemberDialog = ({
  open,
  member,
  onClose,
  onRemoved,
}: RemoveMemberDialogProps) => {
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState('');

  const handleRemove = async () => {
    if (!member) return;
    setRemoving(true);
    setError('');
    try {
      await removeHouseholdMember(member.membershipId);
      onRemoved();
      onClose();
    } catch {
      setError('Could not remove member. Please try again.');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {member?.status === 'PENDING' ? 'Cancel invite?' : 'Remove member?'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography>
          {member?.status === 'PENDING' ? (
            <>
              Cancel the pending invite to <strong>{member?.email}</strong>?
              They will no longer be able to join using the invite link.
            </>
          ) : (
            <>
              Remove <strong>{member?.name || member?.email}</strong> from the
              household? Their personal data remains private and only shared
              data will stop updating.
            </>
          )}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleRemove}
          variant="contained"
          color="error"
          disabled={removing}
        >
          {removing
            ? 'Removing…'
            : member?.status === 'PENDING'
              ? 'Cancel invite'
              : 'Remove'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── OverviewTab ──────────────────────────────────────────────────────────────

interface OverviewTabProps {
  summary: HouseholdSummary | null;
  insights: HouseholdInsight[];
  loading: boolean;
  insightsLoading: boolean;
  error: string;
  insightsError: string;
}

const OverviewTab = ({
  summary,
  insights,
  loading,
  insightsLoading,
  error,
  insightsError,
}: OverviewTabProps) => {
  if (loading) {
    return (
      <Grid container spacing={2}>
        {[1, 2, 3].map((i) => (
          <Grid item xs={12} sm={4} key={i}>
            <Skeleton
              variant="rectangular"
              height={90}
              sx={{ borderRadius: 2 }}
            />
          </Grid>
        ))}
        <Grid item xs={12}>
          <Skeleton
            variant="rectangular"
            height={160}
            sx={{ borderRadius: 2 }}
          />
        </Grid>
      </Grid>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!summary) return null;

  const totalSpend =
    summary.needsSpend + summary.wantsSpend + summary.savingsSpend;
  const netPosition = summary.combinedMonthlyIncome - totalSpend;
  const income = summary.combinedMonthlyIncome;

  const buckets = [
    { label: 'Needs', amount: summary.needsSpend, color: '#159b72' },
    { label: 'Wants', amount: summary.wantsSpend, color: '#EC4899' },
    { label: 'Savings', amount: summary.savingsSpend, color: '#3B82F6' },
  ];

  return (
    <Box>
      {/* KPI row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Typography
                variant="caption"
                sx={{ color: C.muted, display: 'block' }}
              >
                Combined income
              </Typography>
              <Typography variant="h5" fontWeight={700} sx={{ color: C.ink }}>
                {gbp(income)}
              </Typography>
              <Typography variant="caption" sx={{ color: C.muted }}>
                {summary.memberCount} member
                {summary.memberCount !== 1 ? 's' : ''}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Typography
                variant="caption"
                sx={{ color: C.muted, display: 'block' }}
              >
                Total spend this month
              </Typography>
              <Typography variant="h5" fontWeight={700} sx={{ color: C.ink }}>
                {gbp(totalSpend)}
              </Typography>
              <Typography variant="caption" sx={{ color: C.muted }}>
                {income > 0
                  ? `${Math.round((totalSpend / income) * 100)}% of combined income`
                  : '—'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Typography
                variant="caption"
                sx={{ color: C.muted, display: 'block' }}
              >
                Net position
              </Typography>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ color: netPosition >= 0 ? C.green : C.red }}
              >
                {gbp(netPosition)}
              </Typography>
              <Typography variant="caption" sx={{ color: C.muted }}>
                Income minus spend
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Spend breakdown */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 2.5 }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ color: C.ink, mb: 2 }}
          >
            Spend breakdown this month
          </Typography>
          <Grid container spacing={2}>
            {buckets.map((b) => {
              const pct =
                income > 0 ? Math.round((b.amount / income) * 100) : 0;
              return (
                <Grid item xs={12} sm={4} key={b.label}>
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: C.muted }}>
                        {b.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: C.ink }}
                      >
                        {pct}%
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        bgcolor: alpha(b.color, 0.12),
                        borderRadius: 4,
                        height: 6,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          bgcolor: b.color,
                          borderRadius: 4,
                          height: '100%',
                          width: `${Math.min(pct, 100)}%`,
                        }}
                      />
                    </Box>
                    <Typography variant="caption" sx={{ color: C.muted }}>
                      {gbp(b.amount)}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

      {/* Goals and commitments */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6}>
          <Card>
            <CardContent sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} sx={{ color: C.blue }}>
                {summary.activeGoals}
              </Typography>
              <Typography variant="caption" sx={{ color: C.muted }}>
                Active goals
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6}>
          <Card>
            <CardContent sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} sx={{ color: C.blue }}>
                {summary.activeCommitments}
              </Typography>
              <Typography variant="caption" sx={{ color: C.muted }}>
                Active commitments
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Isolation note */}
      <Alert severity="info" sx={{ mb: 3 }}>
        This overview shows combined totals from shared data only. Private
        transactions and personal data remain hidden unless members choose to
        share them in their Sharing settings.
      </Alert>

      {/* Household insights */}
      {insightsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {insightsError}
        </Alert>
      )}
      {insightsLoading ? (
        <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
      ) : insights.length > 0 ? (
        <Box>
          <Typography
            variant="overline"
            sx={{ color: C.muted, mb: 1.5, display: 'block' }}
          >
            Household insights
          </Typography>
          <Grid container spacing={2}>
            {insights.map((insight) => {
              const color = INSIGHT_COLORS[insight.type] ?? C.muted;
              const Icon =
                insight.type === 'POSITIVE'
                  ? CheckCircleOutlineIcon
                  : insight.type === 'WARNING'
                    ? WarningAmberOutlinedIcon
                    : InfoOutlinedIcon;
              return (
                <Grid item xs={12} key={insight.key}>
                  <Card sx={{ borderLeft: `4px solid ${color}` }}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                        }}
                      >
                        <Icon
                          sx={{ color, fontSize: 20, mt: 0.25, flexShrink: 0 }}
                        />
                        <Box>
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            sx={{ color: C.ink }}
                          >
                            {insight.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: C.muted }}>
                            {insight.body}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ) : null}
    </Box>
  );
};

// ─── MembersTab ───────────────────────────────────────────────────────────────

interface MembersTabProps {
  members: HouseholdMember[];
  loading: boolean;
  error: string;
  isOwner: boolean;
  onInvited: () => void;
  onRemoved: () => void;
}

const MembersTab = ({
  members,
  loading,
  error,
  isOwner,
  onInvited,
  onRemoved,
}: MembersTabProps) => {
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'PARTNER' | 'VIEWER'>('PARTNER');
  const [inviting, setInviting] = useState(false);
  const [inviteErr, setInviteErr] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  const [removeOpen, setRemoveOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<HouseholdMember | null>(
    null,
  );

  const handleInvite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      setInviteErr('Enter a valid email address.');
      return;
    }
    setInviting(true);
    setInviteErr('');
    setInviteSuccess('');
    try {
      await inviteToHousehold({ email, role: inviteRole });
      setInviteEmail('');
      setInviteSuccess(`Invite sent to ${email}`);
      onInvited();
    } catch {
      setInviteErr('Could not send invite. Please try again.');
    } finally {
      setInviting(false);
    }
  };

  if (loading) {
    return (
      <Box>
        {[1, 2].map((i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            height={80}
            sx={{ borderRadius: 2, mb: 2 }}
          />
        ))}
      </Box>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;

  const active = members.filter((m) => m.status === 'ACTIVE');
  const pending = members.filter((m) => m.status === 'PENDING');

  return (
    <Box>
      {/* Active members */}
      <Typography
        variant="overline"
        sx={{ color: C.muted, display: 'block', mb: 1 }}
      >
        Members ({active.length})
      </Typography>
      {active.length === 0 ? (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <PeopleOutlinedIcon
              sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
            />
            <Typography variant="body2" sx={{ color: C.muted }}>
              No other members yet. Invite a partner or household member below.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {active.map((m) => {
            const roleColor = ROLE_COLORS[m.role] ?? C.muted;
            const canRemove = isOwner && m.role !== 'OWNER';
            return (
              <Grid item xs={12} key={m.membershipId}>
                <Card>
                  <CardContent
                    sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: alpha(roleColor, 0.15),
                        color: roleColor,
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        width: 44,
                        height: 44,
                      }}
                    >
                      {m.avatarInitials ||
                        (m.name
                          ? m.name.slice(0, 2).toUpperCase()
                          : m.email.slice(0, 2).toUpperCase())}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography
                          variant="body1"
                          fontWeight={600}
                          sx={{ color: C.ink }}
                        >
                          {m.name || m.email}
                        </Typography>
                        <Chip
                          label={
                            m.role.charAt(0) + m.role.slice(1).toLowerCase()
                          }
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            bgcolor: alpha(roleColor, 0.1),
                            color: roleColor,
                          }}
                        />
                      </Box>
                      <Typography variant="caption" sx={{ color: C.muted }}>
                        {m.email}
                      </Typography>
                      {m.monthlyIncome != null && m.monthlyIncome > 0 && (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ color: C.muted, ml: 1 }}
                        >
                          · {gbp(m.monthlyIncome)}/mo
                        </Typography>
                      )}
                    </Box>
                    {canRemove && (
                      <Tooltip title="Remove member">
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => {
                            setRemoveTarget(m);
                            setRemoveOpen(true);
                          }}
                          sx={{ flexShrink: 0 }}
                        >
                          Remove
                        </Button>
                      </Tooltip>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Pending invites */}
      {pending.length > 0 && (
        <>
          <Typography
            variant="overline"
            sx={{ color: C.muted, display: 'block', mb: 1 }}
          >
            Pending invites ({pending.length})
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {pending.map((m) => (
              <Grid item xs={12} key={m.membershipId}>
                <Card sx={{ border: '1px dashed', borderColor: 'divider' }}>
                  <CardContent
                    sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: alpha(C.muted, 0.1),
                        color: C.muted,
                        fontSize: '0.9rem',
                        width: 44,
                        height: 44,
                      }}
                    >
                      {m.email.slice(0, 2).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{ color: C.ink }}
                      >
                        {m.email}
                      </Typography>
                      <Chip
                        label="Pending invite"
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.62rem',
                          bgcolor: alpha(C.orange, 0.1),
                          color: C.orange,
                          mt: 0.5,
                        }}
                      />
                    </Box>
                    {isOwner && (
                      <Tooltip title="Cancel invite">
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => {
                            setRemoveTarget(m);
                            setRemoveOpen(true);
                          }}
                          sx={{ flexShrink: 0 }}
                        >
                          Cancel
                        </Button>
                      </Tooltip>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Invite form */}
      {isOwner && (
        <Card sx={{ bgcolor: alpha(C.blue, 0.03) }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <PersonAddOutlinedIcon sx={{ color: C.blue }} />
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ color: C.ink }}
              >
                Invite someone
              </Typography>
            </Box>
            {inviteErr && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {inviteErr}
              </Alert>
            )}
            {inviteSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {inviteSuccess}
              </Alert>
            )}
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                flexWrap: 'wrap',
                alignItems: 'flex-start',
              }}
            >
              <TextField
                label="Email address"
                size="small"
                type="email"
                value={inviteEmail}
                onChange={(e) => {
                  setInviteEmail(e.target.value);
                  setInviteErr('');
                  setInviteSuccess('');
                }}
                sx={{ flex: 1, minWidth: 220 }}
              />
              <Select
                value={inviteRole}
                onChange={(e) =>
                  setInviteRole(e.target.value as 'PARTNER' | 'VIEWER')
                }
                size="small"
                sx={{ minWidth: 130 }}
              >
                <MenuItem value="PARTNER">Partner</MenuItem>
                <MenuItem value="VIEWER">Viewer</MenuItem>
              </Select>
              <Button
                variant="contained"
                onClick={handleInvite}
                disabled={inviting}
                startIcon={<PersonAddOutlinedIcon />}
                sx={{ height: 40 }}
              >
                {inviting ? 'Sending…' : 'Send invite'}
              </Button>
            </Box>
            <Typography
              variant="caption"
              sx={{ color: C.muted, mt: 1.5, display: 'block' }}
            >
              <strong>Partner</strong> — can add their financial information to
              the household. <strong>Viewer</strong> — read-only access to
              shared data.
            </Typography>
          </CardContent>
        </Card>
      )}

      <RemoveMemberDialog
        open={removeOpen}
        member={removeTarget}
        onClose={() => {
          setRemoveOpen(false);
          setRemoveTarget(null);
        }}
        onRemoved={() => {
          setRemoveOpen(false);
          setRemoveTarget(null);
          onRemoved();
        }}
      />
    </Box>
  );
};

// ─── SharingTab ───────────────────────────────────────────────────────────────

const SharingTab = () => {
  const [sharing, setSharing] = useState<HouseholdSharing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  useEffect(() => {
    getHouseholdSharing()
      .then((res) => setSharing(res.data))
      .catch(() => setError('Could not load sharing settings.'))
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = async (key: keyof HouseholdSharing) => {
    if (!sharing) return;
    const updated = { ...sharing, [key]: !sharing[key] };
    setSharing(updated);
    setSaving(true);
    setSaveMsg('');
    setError('');
    try {
      await updateHouseholdSharing(updated);
      setSaveMsg('Saved');
      setTimeout(() => setSaveMsg(''), 2500);
    } catch {
      setSharing(sharing);
      setError('Could not save preference. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box>
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            height={60}
            sx={{ borderRadius: 2, mb: 1.5 }}
          />
        ))}
      </Box>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!sharing) return null;

  const options: {
    key: keyof HouseholdSharing;
    label: string;
    description: string;
  }[] = [
    {
      key: 'shareAccounts',
      label: 'Bank accounts',
      description: 'Share your connected account names, types and balances.',
    },
    {
      key: 'shareTransactions',
      label: 'Transactions',
      description:
        'Share individual transaction history with household members.',
    },
    {
      key: 'shareGoals',
      label: 'Goals',
      description: 'Share your savings goals and progress.',
    },
    {
      key: 'shareDocuments',
      label: 'Documents',
      description: 'Share uploaded documents with household members.',
    },
    {
      key: 'shareLifeItems',
      label: 'Life items',
      description: 'Share life events and planning items.',
    },
  ];

  return (
    <Box>
      <Typography variant="body2" sx={{ color: C.muted, mb: 3 }}>
        Control what financial data you share with other household members.
        These settings apply to your data only and do not affect what others
        share with you.
      </Typography>

      {saveMsg && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {saveMsg}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          {options.map((opt, idx) => (
            <React.Fragment key={opt.key}>
              {idx > 0 && <Divider />}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2.5,
                  py: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="body1"
                    fontWeight={500}
                    sx={{ color: C.ink }}
                  >
                    {opt.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: C.muted }}>
                    {opt.description}
                  </Typography>
                </Box>
                <Switch
                  checked={sharing[opt.key]}
                  onChange={() => handleToggle(opt.key)}
                  disabled={saving}
                  color="primary"
                />
              </Box>
            </React.Fragment>
          ))}
        </CardContent>
      </Card>

      <Typography
        variant="caption"
        sx={{ color: C.muted, mt: 2, display: 'block' }}
      >
        Changes take effect immediately. Other members control their own sharing
        preferences independently.
      </Typography>
    </Box>
  );
};

// ─── HouseholdPage ────────────────────────────────────────────────────────────

const HouseholdPage = (): JSX.Element => {
  const [tab, setTab] = useState(0);

  const [summary, setSummary] = useState<HouseholdSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState('');

  const [insights, setInsights] = useState<HouseholdInsight[]>([]);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [insightsError, setInsightsError] = useState('');

  const loadSummary = () => {
    setSummaryLoading(true);
    setSummaryError('');
    getHousehold()
      .then((res) => setSummary(res.data))
      .catch(() => setSummaryError('Could not load household data.'))
      .finally(() => setSummaryLoading(false));
  };

  useEffect(() => {
    loadSummary();

    getHouseholdInsights()
      .then((res) => setInsights(res.data))
      .catch(() => setInsightsError('Could not load household insights.'))
      .finally(() => setInsightsLoading(false));
  }, []);

  const members = summary?.members ?? [];
  const currentEmail = getLoggedInUser();
  const currentMember = members.find((m) => m.email === currentEmail);
  const isOwner = currentMember?.role === 'OWNER';
  const activeCount = members.filter((m) => m.status === 'ACTIVE').length;

  return (
    <UserDashboard>
      <FeatureGate featureKey={FeatureKeys.HOUSEHOLD_CORE}>
        <Box
          sx={{
            background: `linear-gradient(175deg, ${alpha(C.blue, 0.05)} 0%, transparent 40%)`,
            minHeight: '100vh',
            py: 4,
          }}
        >
          <Container>
            {/* Header */}
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}
            >
              <PeopleOutlinedIcon sx={{ fontSize: 32, color: C.muted }} />
              <Box>
                <Typography variant="h4" fontWeight={700} sx={{ color: C.ink }}>
                  {summaryLoading
                    ? 'Household'
                    : summary?.householdName || 'Household'}
                </Typography>
                <Typography variant="body2" sx={{ color: C.muted }}>
                  {summaryLoading
                    ? '…'
                    : `${activeCount} active member${activeCount !== 1 ? 's' : ''}`}
                </Typography>
              </Box>
            </Box>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                <Tab label="Overview" />
                <Tab label="Members" />
                <Tab label="Sharing" />
              </Tabs>
            </Box>

            {tab === 0 && (
              <OverviewTab
                summary={summary}
                insights={insights}
                loading={summaryLoading}
                insightsLoading={insightsLoading}
                error={summaryError}
                insightsError={insightsError}
              />
            )}
            {tab === 1 && (
              <MembersTab
                members={members}
                loading={summaryLoading}
                error={summaryError}
                isOwner={isOwner}
                onInvited={loadSummary}
                onRemoved={loadSummary}
              />
            )}
            {tab === 2 && <SharingTab />}
          </Container>
        </Box>
      </FeatureGate>
    </UserDashboard>
  );
};

export default HouseholdPage;
